import { reactive } from 'vue'
import { defineStore } from 'pinia'
import { CommonHttp, CommonRequest, SingleIdRequest } from "@/models/interfaces/http/common.http.request";
import { rootStore } from './index'
import { BillAddAsker, BillAttachAsker, BillEditAsker, BillInfo, BillListGiver, BillNoteEditAsker, BillsAndPaymentsSearchAsker, BillSearchedBase, NewPaymentAsker, PaymentAttachAsker, PaymentEditAsker, PaymentIncomeAttachAsker, PaymentInfo } from '@/models/interfaces/finances/finance-models';
import { CommonListGiver, SingleIdGiver, SingleStringRequest, StringFilterAsker, StringListGiver } from '@/models/interfaces/common';
import { ClientWithCounterpartiesListGiver, CounterpartyAddAsker, CounterpartyAttachAsker, CounterpartyAttachedGiver, CounterpartyExtendedListGiver, CounterpartyInfo, CounterpartySearchedListGiver, ReattachClient } from '@/models/interfaces/finances/counterparty-models';
import { BillConnectionWithValuesEditAsker } from '@/models/interfaces/storage/storage-models';
import { authStore } from '@/store/login';
import { loadSession } from '@/composables/useSession';

const API_URL = process.env.VUE_APP_API_URL
const API_1C_URL = API_URL.slice(0, API_URL.length - 4)

/**
 * Креды для legacy 1C API (ChangerId / SessionKey в теле запроса).
 * rootStore.setJWT() зовётся только из Аналитики — на /main сессия поднимается через
 * applySession(), который ставит лишь токен. Поэтому там rootStore.oldAPIUID пуст,
 * и нужные значения берём из authStore / localStorage.
 */
export function legacy1CCreds(): { ChangerId: any; SessionKey: any } {
  const root = rootStore()
  if (root.oldAPIUID && root.oldAPISessionKey) {
    return { ChangerId: root.oldAPIUID, SessionKey: root.oldAPISessionKey }
  }

  const auth = authStore()
  const session = loadSession()
  return {
    ChangerId: auth.currentUserId ?? session?.id ?? '',
    SessionKey: auth.sessionKey ?? session?.sessionKey ?? '',
  }
}

/** Те же креды, но приведённые к типам CommonRequest (ChangerId: number). */
export function commonRequestCreds(): CommonRequest {
  const creds = legacy1CCreds()
  return {
    ChangerId: Number(creds.ChangerId) || 0,
    SessionKey: String(creds.SessionKey ?? ''),
  }
}

/**
 * Тело для legacy 1C API: JSON с кредами, обёрнутый в одинарные кавычки —
 * формат, который ждёт старый сервис (напр. '{"Filter":"123","ChangerId":682,...}').
 */
function legacy1CBody(arg: Record<string, any>): string {
  return '\'' + JSON.stringify({ ...arg, ...legacy1CCreds() }) + '\''
}

/** Passthrough transformRequest — не даём axios переупаковывать уже готовую строку. */
const legacy1CTransform = [(data: any) => data]

interface QueryState {
  // 1. movementGetBillsForCreation
  movement_bills_search?: BillSearchedBase[]
  // 2. counterpartyAddNew
  counterparty_added?: CounterpartyExtendedListGiver
  // 3. counterpartyAddExisting
  counterparty_attached?: CounterpartyExtendedListGiver
  // 8. counterpartyGetAttachedList
  counterparty_attached_list?: CounterpartyExtendedListGiver
  // 11. getInfoCBill
  // bill_info?: BillInfo
  // 12. getInfoPayment
  // payment_info?: PaymentInfo
  // 13. billAdd
  bill_added_id?: SingleIdGiver
  // 15. projectPaymentAttach
  payment_attached_id?: SingleIdGiver
  // 17. projectPaymentAdd
  payment_created_project?: SingleIdGiver
  // 18. incomePaymentAdd
  payment_created_income?: SingleIdGiver
  // 28. billGetAttachedList
  bill_attached_list?: BillListGiver
  // 29. getProjectCounterparties
  project_counterparties?: ClientWithCounterpartiesListGiver
  // 30. AddFileToBill
  bill_file_added?: CommonListGiver
  // 31. synchronizationGetInstances
  sync_instances?: StringListGiver

  // общие
  loading: boolean
  stop_request: boolean
  reqQueue: Map<string, AbortController>
}

export const financesStore = defineStore('financesStore', {
  state: () => 
  ({
    loading: false,
    stop_request: false,
    reqQueue: new Map(),
  } as QueryState),

  actions: {
    // 1. POST search bills for movement creation
    async movementGetBillsForCreation(arg: BillsAndPaymentsSearchAsker): Promise<BillSearchedBase[]> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'movement_bills_search'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Bill/Search`, arg, { signal: controller.signal })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.movement_bills_search = reactive(response.data as BillSearchedBase[])
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return []
    },

    // 2. POST add new counterparty
    async counterpartyAddNew(arg: CounterpartyAddAsker): Promise<CounterpartyExtendedListGiver> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'counterparty_added'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_1C_URL, rootstore.globalJWT, true)
      try {
        this.loading = true
        const response = await http.post(`1C/Counterparty/add/new`, legacy1CBody(arg), {
          signal: controller.signal,
          transformRequest: legacy1CTransform,
        })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.counterparty_added = reactive(response.data as CounterpartyExtendedListGiver)
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as CounterpartyExtendedListGiver
    },

    // 3. POST add existing counterparty
    async counterpartyAddExisting(arg: { clientId: number; counterpartyId: number }): Promise<CounterpartyAttachedGiver | undefined> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'counterparty_attached'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1c/counter/existing`, arg, { signal: controller.signal })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.loading = false
          return response.data as CounterpartyAttachedGiver
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return undefined
    },

    // 4. POST detach counterparty
    async counterpartyDetach(arg: SingleIdRequest): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_1C_URL, rootstore.globalJWT, true)
      try {
        this.loading = true
        const response = await http.post(`1C/Counterparty/detach`, legacy1CBody(arg), {
          transformRequest: legacy1CTransform,
        })
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e) {
        console.log(e)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 5. POST get clients to reattach
    async getClientsReettach(arg: SingleStringRequest): Promise<ClientWithCounterpartiesListGiver | undefined> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'clients_to_reattach'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL.slice(0, API_URL.length-4), rootstore.globalJWT, true)
      try {
        this.loading = true

        const body = '\''+JSON.stringify({
          Value: arg.Value,
          ChangerId: rootstore.oldAPIUID,
          SessionKey: rootstore.oldAPISessionKey
        })+'\'';

        const response = await http.post(`1C/Counterparty/GetClientsToReattach`, body, { signal: controller.signal,
          // Override the default transformRequest
          transformRequest: [
            (data: any) => {
              // If the data is a string (and presumably already JSON), return it directly.
              if (typeof data === 'string') {
                return data;
              }
              // If it's an object (or other type), you might need to handle it differently
              // or simply return it as is if you are sure it is pre-formatted.
              return data; 
            }
          ]
        })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          // нет поля в state — просто возвращаем
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return undefined
    },

    // 6. POST reattach counterparty
    async counterpartyReettach(arg: CounterpartyAttachAsker): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL.slice(0, API_URL.length-4), rootstore.globalJWT, true)
      try {
        this.loading = true

        const body = '\''+JSON.stringify({
          Id: arg.Id,
          ClientId: arg.ClientId,
          ChangerId: rootstore.oldAPIUID,
          SessionKey: rootstore.oldAPISessionKey
        })+'\'';

        const response = await http.post(`1C/Counterparty/Reattach`, body, {
          // Override the default transformRequest
          transformRequest: [
            (data: any) => {
              // If the data is a string (and presumably already JSON), return it directly.
              if (typeof data === 'string') {
                return data;
              }
              // If it's an object (or other type), you might need to handle it differently
              // or simply return it as is if you are sure it is pre-formatted.
              return data; 
            }
          ]
        })
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e) {
        console.log(e)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 7. POST get filtered counterparty list
    async counterpartyGetFilteredList(arg: any): Promise<CounterpartySearchedListGiver | undefined> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'counterparty_filtered_list'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL.slice(0, API_URL.length-4), rootstore.globalJWT, true)
      try {
        this.loading = true;
        const body = '\''+JSON.stringify({
          ...arg,
          ...legacy1CCreds()
        })+'\'';

        const response = await http.post(`1C/Counterparty/get/filteredList`, body, { signal: controller.signal,
          // Override the default transformRequest
          transformRequest: [
            (data: any) => {
              // If the data is a string (and presumably already JSON), return it directly.
              if (typeof data === 'string') {
                return data;
              }
              // If it's an object (or other type), you might need to handle it differently
              // or simply return it as is if you are sure it is pre-formatted.
              return data; 
            }
          ]
        })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return undefined
    },

    // 8. POST get attached counterparty list
    async counterpartyGetAttachedList(arg: SingleIdRequest): Promise<CounterpartyExtendedListGiver> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'counterparty_attached_list'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_1C_URL, rootstore.globalJWT, true)
      try {
        this.loading = true
        const response = await http.post(`1C/Counterparty/get/AttachedList`, legacy1CBody(arg), {
          signal: controller.signal,
          transformRequest: legacy1CTransform,
        })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.counterparty_attached_list = reactive(response.data as CounterpartyExtendedListGiver)
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as CounterpartyExtendedListGiver
    },

    // 9. POST check new taxpayer number
    async checkNewTaxpayerNumber(arg: SingleStringRequest): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_1C_URL, rootstore.globalJWT, true)
      try {
        this.loading = true
        const response = await http.post(`1C/Counterparty/CheckNewTaxpayerNumber`, legacy1CBody(arg), {
          transformRequest: legacy1CTransform,
        })
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e) {
        console.log(e)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 10. POST get counterparty info
    async getInfoCounterparty(c_id: number): Promise<CounterpartyInfo | undefined> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'counterparty_info'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.get(`1c/counter/info/${c_id}`)
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return undefined
    },

    // 11. GET bill info
    async getInfoCBill(id: number): Promise<BillInfo> {
      const rootstore = rootStore()
      this.stop_request = false
      // const entkey = 'bill_info'
      // const prev = this.reqQueue.get(entkey)
      // if (prev) prev.abort()
      // const controller = new AbortController()
      // this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.get(`1C/Bill/GetInfo/${id}`)
        // this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          const { data } = response
          this.loading = false
          return reactive(data as BillInfo)
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        // this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as BillInfo
    },

    // 12. GET payment info
    async getInfoPayment(id: number): Promise<PaymentInfo> {
      const rootstore = rootStore()
      this.stop_request = false
      // const entkey = 'payment_info'
      // const prev = this.reqQueue.get(entkey)
      // if (prev) prev.abort()
      // const controller = new AbortController()
      // this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.get(`1C/Payment/GetInfo/${id}`)
        // this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          const { data } = response;
          // this.payment_info = reactive(response.data as PaymentInfo)
          this.loading = false
          return reactive(data as PaymentInfo)
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        // this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as PaymentInfo
    },

    // 13. POST attach bill to project
    async billAdd(arg: BillAddAsker): Promise<SingleIdGiver> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'bill_added_id'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Bill/AttachToProject`, arg, { signal: controller.signal })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.bill_added_id = reactive(response.data as SingleIdGiver)
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as SingleIdGiver
    },

    // 14. POST attach bill to income
    async attachBillToIncome(arg: BillAttachAsker): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Bill/AttachToIncome`, arg)
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e) {
        console.log(e)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 15. POST attach payment to project
    async projectPaymentAttach(arg: PaymentAttachAsker): Promise<SingleIdGiver> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'payment_attached_id'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Payment/AttachToProject`, arg, { signal: controller.signal })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.payment_attached_id = reactive(response.data as SingleIdGiver)
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as SingleIdGiver
    },

    // 16. POST attach payment to income
    async attachPaymentToIncome(arg: PaymentIncomeAttachAsker): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Payment/AttachToIncome`, arg)
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e) {
        console.log(e)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 17. POST create payment in project
    async projectPaymentAdd(arg: NewPaymentAsker): Promise<SingleIdGiver> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'payment_created_project'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Payment/CreateInProject`, arg, { signal: controller.signal })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.payment_created_project = reactive(response.data as SingleIdGiver)
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as SingleIdGiver
    },

    // 18. POST create payment in income
    async incomePaymentAdd(arg: NewPaymentAsker): Promise<SingleIdGiver> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'payment_created_income'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Payment/CreateInIncome`, arg, { signal: controller.signal })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.payment_created_income = reactive(response.data as SingleIdGiver)
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as SingleIdGiver
    },

    // 19. PUT Api/1C/Payment/Edit (без slice/session key, по аналогии с billEdit)
    async paymentEdit(arg: PaymentEditAsker): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'payment_edit'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.put(`1C/Payment/Edit`, arg, { signal: controller.signal })
        this.reqQueue.delete(entkey)
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 20. POST edit bill
    async billEdit(arg: BillEditAsker): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Bill/edit`, arg)
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e) {
        console.log(e)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 21. POST detach bill from project
    async billDetach(arg: BillAttachAsker): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Bill/DetachFromProject`, arg)
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e) {
        console.log(e)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 22. POST detach payment from project
    async paymentDetach(arg: SingleIdRequest): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Payment/DetachFromProject`, arg)
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e) {
        console.log(e)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 23. POST detach bill from income
    async billDetachFromIncome(arg: BillAttachAsker): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Bill/DetachFromIncome`, arg)
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e) {
        console.log(e)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 24. POST detach payment from income
    async paymentDetachFromIncome(arg: SingleIdRequest): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Payment/DetachFromIncome`, arg)
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e) {
        console.log(e)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 25. PUT edit bill connections
    async updateConnectionValues(arg: BillConnectionWithValuesEditAsker): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.put(`1C/Bill/EditConnections`, arg)
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e) {
        console.log(e)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 26. POST edit bill note
    async billEditNote(arg: BillNoteEditAsker): Promise<{ response_ok: boolean }> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Bill/EditNote`, arg)
        this.loading = false
        return { response_ok: response.status === 200 || response.status === 201 }
      } catch (e) {
        console.log(e)
        this.loading = false
        return { response_ok: false }
      }
    },

    // 28. POST get attached bill list
    async billGetAttachedList(arg: SingleIdRequest): Promise<BillListGiver> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'bill_attached_list'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Bill/get/AttachedToProjectList`, arg, { signal: controller.signal })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.bill_attached_list = reactive(response.data as BillListGiver)
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as BillListGiver
    },

    // 29. POST get project counterparties
    async getProjectCounterparties(arg: SingleIdRequest): Promise<ClientWithCounterpartiesListGiver | undefined> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'project_counterparties'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL.slice(0, API_URL.length-4), rootstore.globalJWT, true)
      try {
        this.loading = true

        const body = '\''+JSON.stringify({
          Id: arg.Id,
          ChangerId: rootstore.oldAPIUID,
          SessionKey: rootstore.oldAPISessionKey
        })+'\'';

        const res = await http.post(`1C/Bill/get/ProjectCounterparties`, body, { signal: controller.signal,
          // Override the default transformRequest
          transformRequest: [
            (data: any) => {
              // If the data is a string (and presumably already JSON), return it directly.
              if (typeof data === 'string') {
                return data;
              }
              // If it's an object (or other type), you might need to handle it differently
              // or simply return it as is if you are sure it is pre-formatted.
              return data; 
            }
          ]
        });
        
        this.reqQueue.delete(entkey);

        if (res.status === 200 || res.status === 201) {
          this.loading = false
          return res.data;
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return undefined
    },

    // 30. POST add file to bill
    async AddFileToBill(formData: FormData): Promise<CommonListGiver> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'bill_file_added'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.post(`1C/Bill/AddFileToBill`, formData, { signal: controller.signal })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.bill_file_added = reactive(response.data as CommonListGiver)
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as CommonListGiver
    },

    // 31. POST get synchronization instances
    async synchronizationGetInstances(arg: number): Promise<StringListGiver> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'sync_instances'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL.slice(0, API_URL.length-4), rootstore.globalJWT, true)
      try {
        this.loading = true

        const body = '\''+JSON.stringify({
          Id: arg,
          ChangerId: rootstore.oldAPIUID,
          SessionKey: rootstore.oldAPISessionKey
        })+'\'';

        const response = await http.post(`1C/Synchronization/GetInstances`, body, { signal: controller.signal,
          // Override the default transformRequest
          transformRequest: [
            (data: any) => {
              // If the data is a string (and presumably already JSON), return it directly.
              if (typeof data === 'string') {
                return data;
              }
              // If it's an object (or other type), you might need to handle it differently
              // or simply return it as is if you are sure it is pre-formatted.
              return data; 
            }
          ]
        })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.sync_instances = reactive(response.data as StringListGiver)
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as StringListGiver
    },

    // 32. GET synchronization instances (new API, без привязки к проекту)
    async getSynchronizationInstances(): Promise<StringListGiver> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'sync_instances'
      const prev = this.reqQueue.get(entkey)
      if (prev) prev.abort()
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.get(`1C/synchronization/instances`, { signal: controller.signal })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.sync_instances = reactive(response.data as StringListGiver)
          this.loading = false
          return response.data
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') console.log(e)
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return {} as StringListGiver
    },

    // 33. GET список клиентов для перепривязки КА
    async getReattachClients(filter: string, count?: number, offset?: number): Promise<ReattachClient[]> {
      const rootstore = rootStore()
      this.stop_request = false
      const entkey = 'clients_to_reattach'
      const prev = this.reqQueue.get(entkey)
      if (prev) {
        prev.abort()
      }
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const params: any = { filter }
      if (count !== undefined) params.count = count
      if (offset !== undefined) params.offset = offset

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.get(`1C/counterparties/clients-to-reattach`, {
          params,
          signal: controller.signal,
        })
        this.reqQueue.delete(entkey)

        if (response.status === 200 || response.status === 201) {
          this.loading = false
          return Array.isArray(response.data) ? response.data : []
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          console.log(e)
        }
        this.reqQueue.delete(entkey)
        this.loading = false
      }
      return []
    },

    // 34. PATCH перепривязка КА к другому клиенту
    async reattachCounterparty(arg: { counterpartyId: number; clientId: number }): Promise<boolean> {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        const response = await http.patch(`1C/counterparties/${arg.counterpartyId}/reattach`, arg)
        this.loading = false
        return [200, 201, 204].includes(response.status)
      } catch (e) {
        console.log(e)
        this.loading = false
        return false
      }
    },
  },

  getters: {
    isLoading(state): boolean {
        return state.loading
    },
  },
});
